import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

// Written by the managed OAuth flow's metadata, not a step input.
const apiEndPoint = (configuration: object) =>
  (configuration as Record<string, string>)["installation.apiEndPoint"];

export const teamwork = defineConnector({
  identifier: "teamwork",
  name: "Teamwork",
  tagLine: "Project and client work management",
  logo,
  protocol: "http",
  tags: ["productivity", "it"],
  defaultUrlPattern: (configuration) =>
    `${apiEndPoint(configuration)?.replaceAll(".", "\\.") ?? ""}/.*`,
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Teamwork app",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Teamwork authentication guide](https://apidocs.teamwork.com/guides/teamwork/authentication) and create an OAuth app for the Teamwork site you want to connect. Copy the client ID and client secret, and note the installation API endpoint for your site.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/teamwork/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Teamwork account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Teamwork account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://www.teamwork.com/launchpad/login",
    tokenEndpoint: () => "https://www.teamwork.com/launchpad/v1/token.json",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) => `${apiEndPoint(configuration) ?? ""}/`,
  },
});
